import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Send, Plus, Pencil, ShieldCheck, ShieldAlert, Building2, Percent, FileBadge, Hash, Mail, AlertTriangle, Printer, Users as UsersIcon, Database, Receipt, Wifi, WifiOff, Truck, CreditCard, FileClock, ScanBarcode, Star, StarOff, X as XIcon } from "lucide-react";
import FieldHint from "@/components/FieldHint";
import CustomerCombobox from "@/components/CustomerCombobox";
import { MigrationContent } from "@/pages/Migration";
import { useConfirm } from "@/components/ConfirmDialog";
import DGIICertificationTab from "@/components/DGIICertificationTab";
import ACECFCertificationTab from "@/components/ACECFCertificationTab";
import { PurgeNonElectronicCard } from "@/components/PurgeNonElectronicCard";
import Phase4CertificationTab from "@/components/Phase4CertificationTab";
import DgiiRncRegistryPanel from "@/components/DgiiRncRegistryPanel";
import ModulesTab from "@/components/ModulesTab";
import ExpenseCategoriesTab from "@/components/ExpenseCategoriesTab";
import { FileCheck2, Package2, Tag } from "lucide-react";

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-card border border-border rounded-md p-6">
      <div className="overline mb-1">Configuración</div>
      <h3 className="text-2xl font-bold tracking-tight mb-1">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground mb-6">{subtitle}</p>}
      {children}
    </div>
  );
}

function CompanyTab() {
  const [data, setData] = useState({ name: "", rnc: "", address: "", phone: "", email: "", currency: "DOP", logo_url: "" });

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => data && setData((d) => ({ ...d, ...data })));
  }, []);

  const save = async () => {
    try { await api.put("/settings/company", data); toast.success("Datos guardados"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Section title="Datos de la empresa" subtitle="Información que aparecerá en facturas, conduces y reportes.">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2"><FieldHint label="Nombre comercial" hint="Aparecerá en facturas, cotizaciones, conduces y reportes." /><Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="mt-1" data-testid="company-name" /></div>
        <div><FieldHint label="RNC" hint="Registro Nacional del Contribuyente. 9 dígitos. Es el número fiscal con que estás registrado en la DGII." /><Input value={data.rnc} onChange={(e) => setData({ ...data, rnc: e.target.value })} className="mt-1 font-mono" /></div>
        <div><FieldHint label="Teléfono" hint="Aparece en las facturas para contacto del cliente." /><Input value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} className="mt-1 font-mono" /></div>
        <div className="col-span-2"><FieldHint label="Dirección" hint="Domicilio fiscal del negocio." /><Input value={data.address} onChange={(e) => setData({ ...data, address: e.target.value })} className="mt-1" /></div>
        <div><FieldHint label="Email" hint="Correo principal del negocio. Sirve para notificaciones del sistema." /><Input value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="mt-1" /></div>
        <div><FieldHint label="Moneda" hint="Código ISO de la moneda. Por defecto DOP (peso dominicano)." /><Input value={data.currency} onChange={(e) => setData({ ...data, currency: e.target.value })} className="mt-1 font-mono" /></div>
      </div>
      <div className="mt-6"><Button onClick={save} className="bg-primary font-bold" data-testid="save-company-btn">Guardar cambios</Button></div>
    </Section>
  );
}

function POSTab() {
  const [defaultCustomer, setDefaultCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers/default");
      setDefaultCustomer(data || null);
    } catch (e) {
      setDefaultCustomer(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setDefault = async (c) => {
    if (!c) return;
    try {
      await api.post(`/customers/default/${c.id}`);
      toast.success(`${c.name} ahora aparece por defecto en el POS`);
      setDefaultCustomer(c);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearDefault = async () => {
    try {
      await api.delete("/customers/default");
      toast.success("Ya no hay cliente por defecto");
      setDefaultCustomer(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section
      title="Cliente por defecto en POS"
      subtitle="El cliente elegido aquí se cargará automáticamente cuando abras el punto de venta. Los cajeros pueden cambiarlo antes de facturar."
    >
      <div className="space-y-4 max-w-xl">
        <div>
          <Label className="overline mb-1.5 block">Cliente actual</Label>
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando…</div>
          ) : defaultCustomer ? (
            <div
              className="flex items-center justify-between gap-3 border border-primary/40 bg-primary/5 rounded-md p-3"
              data-testid="pos-default-customer"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Star className="w-5 h-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {defaultCustomer.code && (
                      <span className="text-primary data-cell mr-1.5">{defaultCustomer.code}</span>
                    )}
                    {defaultCustomer.name}
                  </div>
                  <div className="text-xs text-muted-foreground data-cell">
                    {defaultCustomer.rnc_cedula || "Sin RNC"}
                    {defaultCustomer.phone && ` · ${defaultCustomer.phone}`}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clearDefault} data-testid="pos-default-clear">
                <XIcon className="w-4 h-4 mr-1" /> Quitar
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">
              <StarOff className="w-4 h-4" />
              Sin cliente por defecto — el cajero deberá elegirlo en cada venta.
            </div>
          )}
        </div>

        <div>
          <Label className="overline mb-1.5 block">Elegir cliente por defecto</Label>
          <CustomerCombobox
            value={null}
            onChange={setDefault}
            testid="pos-default-picker"
            placeholder="Busca por código, nombre, RNC o teléfono…"
            showClear={false}
          />
          <FieldHint>
            Puedes buscar por el código secuencial (por ejemplo <code className="font-mono">CLI-000001</code>), por RNC, cédula o parte del nombre. El listado devuelve hasta 500 clientes por búsqueda.
          </FieldHint>
        </div>
      </div>
    </Section>
  );
}

function TaxTab_MOVED() {}


function TaxTab() {
  const [data, setData] = useState({ itbis_rate: 18, include_itbis_in_price: false });
  useEffect(() => { api.get("/settings/tax").then(({ data }) => data && setData(data)); }, []);
  const save = async () => { try { await api.put("/settings/tax", { ...data, itbis_rate: Number(data.itbis_rate) }); toast.success("Guardado"); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <Section title="Impuestos" subtitle="Configura la tasa de ITBIS aplicada a productos.">
      <div className="grid grid-cols-2 gap-4">
        <div><FieldHint label="Tasa ITBIS (%)" hint="Impuesto a las Transferencias de Bienes Industrializados y Servicios. En RD el general es 18%." /><Input type="number" step="0.01" value={data.itbis_rate} onChange={(e) => setData({ ...data, itbis_rate: e.target.value })} className="mt-1 font-mono" /></div>
        <div className="flex items-center gap-3 pt-5"><Switch checked={data.include_itbis_in_price} onCheckedChange={(v) => setData({ ...data, include_itbis_in_price: v })} /><FieldHint label="Precio incluye ITBIS" hint="Si los precios capturados ya incluyen el ITBIS (descompone el impuesto). Por defecto los precios son sin ITBIS." /></div>
      </div>
      <div className="mt-6"><Button onClick={save} className="bg-primary font-bold">Guardar</Button></div>
    </Section>
  );
}

function CreditTab() {
  const [data, setData] = useState({ default_days: 30 });
  useEffect(() => { api.get("/settings/credit").then(({ data }) => data && setData((d) => ({ ...d, ...data }))); }, []);
  const save = async () => {
    try {
      await api.put("/settings/credit", { default_days: Number(data.default_days) });
      toast.success("Guardado");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Section
      title="Crédito por defecto"
      subtitle="Días de plazo aplicados a facturas a crédito cuando el cliente no tiene un plazo propio configurado."
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldHint
            label="Días de crédito (default global)"
            hint="Al emitir una factura a crédito: si el cliente tiene 'Días de crédito' > 0 en su ficha, ese valor manda. Si no, se usa este default global. La factura calcula la fecha de vencimiento automáticamente."
          />
          <Input
            type="number"
            min={0}
            value={data.default_days}
            onChange={(e) => setData({ ...data, default_days: e.target.value })}
            className="mt-1 font-mono"
            data-testid="credit-default-days"
          />
        </div>
      </div>
      <div className="mt-6">
        <Button onClick={save} className="bg-primary font-bold" data-testid="save-credit-btn">Guardar</Button>
      </div>
    </Section>
  );
}

function PricingTab() {
  const [data, setData] = useState({ margin_standard_pct: 30, margin_min_pct: 5 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/pricing").then(({ data }) => data && setData(data));
  }, []);
  const save = async () => {
    if (Number(data.margin_min_pct) < 0 || Number(data.margin_standard_pct) < Number(data.margin_min_pct)) {
      toast.error("El margen estándar debe ser mayor o igual al mínimo");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/pricing", {
        margin_standard_pct: Number(data.margin_standard_pct),
        margin_min_pct: Number(data.margin_min_pct),
      });
      toast.success("Política de márgenes actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4 max-w-xl" data-testid="pricing-settings">
      <p className="text-sm text-muted-foreground">
        Define los márgenes de beneficio (markup sobre costo) aplicados al fijar precios de venta.
        Cuando un precio esté entre el mínimo y el estándar, el sistema requerirá la contraseña de un administrador para autorizar.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase font-bold">Margen estándar (%)</Label>
          <Input
            type="number" step="0.1" min="0" max="500"
            value={data.margin_standard_pct}
            onChange={(e) => setData({ ...data, margin_standard_pct: e.target.value })}
            className="mt-1 font-mono"
            data-testid="pricing-standard"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Por defecto <b>30%</b>. Precios con margen mayor o igual a este pasan sin advertencia.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase font-bold">Margen mínimo absoluto (%)</Label>
          <Input
            type="number" step="0.1" min="0" max="100"
            value={data.margin_min_pct}
            onChange={(e) => setData({ ...data, margin_min_pct: e.target.value })}
            className="mt-1 font-mono"
            data-testid="pricing-min"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Por defecto <b>5%</b>. Cualquier precio por debajo es rechazado.
          </p>
        </div>
      </div>
      <div className="p-3 bg-muted/40 rounded-md text-xs space-y-1">
        <div><b>≥ {Number(data.margin_standard_pct || 30)}%:</b> autorizado sin advertencia</div>
        <div><b>{Number(data.margin_min_pct || 5)}% – {Number(data.margin_standard_pct || 30)}%:</b> pide contraseña admin</div>
        <div><b>&lt; {Number(data.margin_min_pct || 5)}%:</b> bloqueado</div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-primary font-bold" data-testid="pricing-save">
        {saving ? "Guardando…" : "Guardar política"}
      </Button>
    </div>
  );
}


function QuotationTab() {
  const [data, setData] = useState({ default_valid_days: 14, max_valid_days: 30 });
  useEffect(() => { api.get("/settings/quotation").then(({ data }) => data && setData((d) => ({ ...d, ...data }))); }, []);
  const save = async () => {
    try {
      await api.put("/settings/quotation", {
        default_valid_days: Number(data.default_valid_days),
        max_valid_days: Number(data.max_valid_days),
      });
      toast.success("Guardado");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Section
      title="Cotizaciones"
      subtitle="Vigencia de las cotizaciones. Al vencerse, no podrán convertirse en factura hasta que se cree una nueva."
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldHint
            label="Validez por defecto (días)"
            hint="Se aplica automáticamente al crear una cotización si el usuario no cambia el valor. Máximo global 30 días."
          />
          <Input
            type="number"
            min={1}
            max={Number(data.max_valid_days) || 30}
            value={data.default_valid_days}
            onChange={(e) => setData({ ...data, default_valid_days: e.target.value })}
            className="mt-1 font-mono"
            data-testid="quotation-default-days"
          />
        </div>
        <div>
          <FieldHint
            label="Máximo permitido (días)"
            hint="Tope absoluto: ninguna cotización podrá tener una vigencia mayor. Máximo global permitido: 30 días."
          />
          <Input
            type="number"
            min={1}
            max={30}
            value={data.max_valid_days}
            onChange={(e) => setData({ ...data, max_valid_days: e.target.value })}
            className="mt-1 font-mono"
            data-testid="quotation-max-days"
          />
        </div>
      </div>
      <div className="mt-6">
        <Button onClick={save} className="bg-primary font-bold" data-testid="save-quotation-btn">Guardar</Button>
      </div>
    </Section>
  );
}

function LowStockTab() {
  const [data, setData] = useState({ default_threshold: 5, alert_enabled: true });
  useEffect(() => { api.get("/settings/low-stock").then(({ data }) => data && setData(data)); }, []);
  const save = async () => { try { await api.put("/settings/low-stock", { ...data, default_threshold: Number(data.default_threshold) }); toast.success("Guardado"); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <Section title="Alertas de existencia" subtitle="Umbral por defecto. Cada producto puede tener su propio mínimo.">
      <div className="grid grid-cols-2 gap-4">
        <div><Label className="overline">Umbral por defecto</Label><Input type="number" value={data.default_threshold} onChange={(e) => setData({ ...data, default_threshold: e.target.value })} className="mt-1 font-mono" data-testid="lowstock-threshold" /></div>
        <div className="flex items-center gap-3 pt-5"><Switch checked={data.alert_enabled} onCheckedChange={(v) => setData({ ...data, alert_enabled: v })} data-testid="lowstock-alert-switch" /><Label>Alertas activas</Label></div>
      </div>
      <div className="mt-6"><Button onClick={save} className="bg-primary font-bold">Guardar</Button></div>
    </Section>
  );
}


const PRINT_DOC_TYPES = [
  { key: "invoice", label: "Facturas", hint: "Factura de venta con NCF (e-CF: E31, E32…)" },
  { key: "quotation", label: "Cotizaciones", hint: "Documento previo a la venta para enviar al cliente." },
  { key: "credit_note", label: "Notas de Crédito", hint: "Devolución total o parcial de una factura." },
  { key: "debit_note", label: "Notas de Débito", hint: "Cargo adicional sobre una factura ya emitida." },
  { key: "delivery_note", label: "Conduces / Notas de entrega", hint: "Documento de despacho. Para firma del receptor recomendamos Carta." },
  { key: "payment", label: "Recibos de pago", hint: "Comprobante de cobro o pago contra factura/compra." },
];

function QZTrayTab() {
  const [data, setData] = useState({
    enabled: false, printer_name: "", printer_host: "", printer_port: 9100, encoding: "CP850",
  });
  const [printers, setPrinters] = useState([]);
  const [qzStatus, setQzStatus] = useState("disconnected"); // disconnected | connecting | connected | error
  const [qzError, setQzError] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/settings/qz-tray").then(({ data }) => data && setData((d) => ({ ...d, ...data })));
  }, []);

  const save = async () => {
    try {
      await api.put("/settings/qz-tray", { ...data, printer_port: Number(data.printer_port) || 9100 });
      const { invalidatePrintConfig } = await import("@/components/PrintMenu");
      invalidatePrintConfig();
      toast.success("Configuración QZ guardada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const testConnection = async () => {
    setQzStatus("connecting"); setQzError(""); setTesting(true);
    try {
      const { connectQZ, listPrinters } = await import("@/lib/qzPrint");
      await connectQZ();
      setQzStatus("connected");
      const list = await listPrinters();
      setPrinters(Array.isArray(list) ? list : (list ? [list] : []));
      toast.success("Conectado a QZ Tray");
    } catch (err) {
      setQzStatus("error");
      setQzError(err?.message || String(err));
      toast.error("No se pudo conectar a QZ Tray");
    } finally { setTesting(false); }
  };

  return (
    <Section
      title="QZ Tray — Impresión térmica directa"
      subtitle="QZ Tray es un programa gratuito que corre en cada PC del mostrador y envía los tickets ESC/POS directo a la impresora térmica por TCP. Elimina el desperdicio de papel del driver de Windows."
    >
      <div className="rounded-md bg-info/10 border border-info/30 p-3 mb-5 text-sm">
        <b>¿Por qué QZ Tray?</b> El servidor de la app está en AWS — no puede ver tu impresora directamente.
        QZ Tray corre en tu PC local (la que tiene la app abierta) y recibe los comandos ESC/POS desde el navegador, enviándolos por TCP a la impresora térmica en tu LAN.
        Consulta el <b>Centro de Ayuda → Apéndice — Impresora térmica (QZ Tray)</b> para el tutorial completo de instalación.
      </div>

      {/* Toggle enabled */}
      <div className="flex items-center justify-between gap-4 mb-5 p-3 rounded-md border border-primary/30 bg-primary/5">
        <div>
          <div className="font-semibold text-sm">Habilitar QZ Tray para tickets</div>
          <div className="text-xs text-muted-foreground">
            Al activar, todos los documentos térmicos (factura, NC/ND, recibo, etc.) se enviarán por QZ Tray.
            Los documentos en formato Carta seguirán usando el driver normal del navegador.
          </div>
        </div>
        <Switch checked={!!data.enabled} onCheckedChange={(v) => setData((d) => ({ ...d, enabled: !!v }))} data-testid="qz-enabled-toggle" />
      </div>

      {/* Test connection */}
      <div className="flex items-center gap-3 mb-5 p-3 rounded-md border border-border">
        <div className="flex-1">
          <div className="font-semibold text-sm flex items-center gap-2">
            {qzStatus === "connected" ? <Wifi className="w-4 h-4 text-success" /> :
             qzStatus === "error" ? <WifiOff className="w-4 h-4 text-destructive" /> :
             <Wifi className="w-4 h-4 text-muted-foreground" />}
            Estado: {qzStatus === "connected" ? "Conectado" : qzStatus === "connecting" ? "Conectando…" : qzStatus === "error" ? "Error" : "Desconectado"}
          </div>
          {qzError && <div className="text-xs text-destructive mt-1 break-all">{qzError}</div>}
          {printers.length > 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              Impresoras detectadas: {printers.join(", ")}
            </div>
          )}
        </div>
        <Button onClick={testConnection} variant="outline" disabled={testing} data-testid="qz-test-connection">
          {testing ? "Conectando…" : "Probar conexión"}
        </Button>
      </div>

      {/* Config impresora */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Nombre de impresora (Windows)</Label>
          <Input
            value={data.printer_name}
            onChange={(e) => setData((d) => ({ ...d, printer_name: e.target.value }))}
            placeholder="Ej: Bematech LR1100E"
            className="mt-1"
            data-testid="qz-printer-name"
          />
          <div className="text-xs text-muted-foreground mt-1">
            Nombre tal como aparece en Windows. Usa "Probar conexión" para descubrir las impresoras disponibles.
          </div>
        </div>
        <div>
          <Label className="overline">— o IP de la impresora (TCP raw)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={data.printer_host}
              onChange={(e) => setData((d) => ({ ...d, printer_host: e.target.value }))}
              placeholder="192.168.1.50"
              className="flex-1"
              data-testid="qz-printer-host"
            />
            <Input
              type="number"
              value={data.printer_port}
              onChange={(e) => setData((d) => ({ ...d, printer_port: e.target.value }))}
              placeholder="9100"
              className="w-24"
              data-testid="qz-printer-port"
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Si tu impresora tiene IP fija en la LAN, esta opción es más rápida (puerto estándar 9100).
          </div>
        </div>
        <div>
          <Label className="overline">Codificación de caracteres</Label>
          <Select value={data.encoding} onValueChange={(v) => setData((d) => ({ ...d, encoding: v }))}>
            <SelectTrigger className="mt-1" data-testid="qz-encoding"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CP850">CP850 (recomendado — soporta acentos)</SelectItem>
              <SelectItem value="CP437">CP437 (clásico americano)</SelectItem>
              <SelectItem value="CP858">CP858 (Euro)</SelectItem>
              <SelectItem value="UTF-8">UTF-8 (depende del modelo)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <Button onClick={save} className="bg-primary font-bold" data-testid="qz-save-btn">
          Guardar configuración
        </Button>
        <a href="/help" className="text-sm text-primary hover:underline">📖 Ver tutorial completo de QZ Tray →</a>
      </div>
    </Section>
  );
}


function PrintFormatTab() {
  const [data, setData] = useState({
    invoice: "ticket80", quotation: "letter", credit_note: "ticket80",
    debit_note: "ticket80", delivery_note: "letter", payment: "ticket80",
    direct_print: true,
  });
  useEffect(() => {
    api.get("/settings/print-format").then(({ data }) => data && setData((d) => ({ ...d, ...data })));
  }, []);
  const save = async () => {
    try {
      await api.put("/settings/print-format", data);
      // invalida cache global del PrintMenu
      const { invalidatePrintConfig } = await import("@/components/PrintMenu");
      invalidatePrintConfig();
      toast.success("Formatos guardados");
    }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Section
      title="Formatos de impresión"
      subtitle="Elige el formato por defecto al imprimir cada tipo de documento."
    >
      <div className="rounded-md bg-muted/40 border border-border/60 p-3 mb-5 text-xs text-muted-foreground">
        <b>Bematech LR1100E</b> — Impresora térmica de 80 mm (72 mm imprimibles, 203 dpi). Selecciona <b>Ticket 80mm</b> para documentos cortos de mostrador y <b>Carta (8.5 × 11")</b> para documentos formales que envías al cliente.
      </div>

      {/* Toggle modo directo */}
      <div className="flex items-center justify-between gap-4 mb-5 p-3 rounded-md border border-primary/30 bg-primary/5">
        <div>
          <div className="font-semibold text-sm">Impresión directa (1 clic)</div>
          <div className="text-xs text-muted-foreground">
            Al activar, el botón de imprimir envía directamente al formato preconfigurado y abre el diálogo de impresión del navegador. Si está desactivado, podrás elegir formato (Ticket / Carta) en cada documento.
          </div>
        </div>
        <Switch
          checked={!!data.direct_print}
          onCheckedChange={(v) => setData((d) => ({ ...d, direct_print: !!v }))}
          data-testid="print-direct-toggle"
        />
      </div>

      <div className="space-y-3">
        {PRINT_DOC_TYPES.map((t) => (
          <div key={t.key} className="grid grid-cols-1 md:grid-cols-[1fr,220px] gap-3 items-center border-b border-border/40 pb-3">
            <div>
              <div className="font-semibold text-sm">{t.label}</div>
              <div className="text-xs text-muted-foreground">{t.hint}</div>
            </div>
            <Select
              value={data[t.key]}
              onValueChange={(v) => setData((d) => ({ ...d, [t.key]: v }))}
            >
              <SelectTrigger data-testid={`print-format-${t.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ticket80">Ticket 80mm (Bematech)</SelectItem>
                <SelectItem value="letter">Carta (8.5 × 11")</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <Button onClick={save} className="bg-primary font-bold" data-testid="save-print-format-btn">
          Guardar formatos
        </Button>
      </div>
    </Section>
  );
}

function EmailTab() {
  const [data, setData] = useState({ provider: "resend", resend_api_key: "", sender_email: "onboarding@resend.dev" });
  const [masked, setMasked] = useState("");
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.get("/settings/email").then(({ data: cfg }) => {
      if (cfg) {
        setData((d) => ({ ...d, provider: cfg.provider || "resend", sender_email: cfg.sender_email || d.sender_email }));
        setMasked(cfg.resend_api_key_masked || "");
      }
    });
  }, []);

  const save = async () => { try { await api.put("/settings/email", data); toast.success("Configuración guardada"); setData((d) => ({ ...d, resend_api_key: "" })); } catch (err) { toast.error(formatApiError(err)); } };
  const test = async () => {
    try {
      const { data: r } = await api.post("/settings/email/test", { to: testTo });
      if (r.sent) toast.success("Email enviado correctamente"); else toast.error(`No enviado: ${r.reason}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Section title="Servicio de email" subtitle="Configura Resend para envío de recuperación de contraseña y notificaciones. Obtén tu API key en https://resend.com">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="overline">Proveedor</Label>
          <Select value={data.provider} onValueChange={(v) => setData({ ...data, provider: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="resend">Resend (recomendado)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="overline">Email remitente</Label><Input value={data.sender_email} onChange={(e) => setData({ ...data, sender_email: e.target.value })} className="mt-1" /></div>
        <div className="col-span-2">
          <Label className="overline">API Key {masked && <span className="text-muted-foreground normal-case ml-2 font-mono">{masked}</span>}</Label>
          <Input
            type="password"
            value={data.resend_api_key}
            onChange={(e) => setData({ ...data, resend_api_key: e.target.value })}
            placeholder={masked ? "Dejar vacío para mantener la actual" : "re_xxxxxxxxxxxxxxxxxxxxxxxx"}
            className="mt-1 font-mono"
            data-testid="email-api-key"
          />
        </div>
      </div>
      <div className="mt-6 flex gap-2"><Button onClick={save} className="bg-primary font-bold" data-testid="save-email-btn">Guardar</Button></div>

      <div className="mt-8 pt-6 border-t border-border">
        <Label className="overline">Probar envío</Label>
        <div className="flex gap-2 mt-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email@destinatario.com" className="flex-1" />
          <Button onClick={test} variant="outline"><Send className="w-4 h-4 mr-1.5" /> Enviar prueba</Button>
        </div>
      </div>
    </Section>
  );
}

function DGIITab() {
  const [data, setData] = useState({ environment: "test", rnc_emisor: "", razon_social: "", auto_send_ecf: false });
  const [certInfo, setCertInfo] = useState({ configured: false });
  const [certPwd, setCertPwd] = useState("");
  const [certEnv, setCertEnv] = useState("TesteCF");
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const reload = async () => {
    const [{ data: dgii }, { data: cert }] = await Promise.all([
      api.get("/settings/dgii"),
      api.get("/dgii/certificate"),
    ]);
    if (dgii) setData((prev) => ({ ...prev, ...dgii }));
    setCertInfo(cert || { configured: false });
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      await api.put("/settings/dgii", {
        environment: data.environment, rnc_emisor: data.rnc_emisor,
        razon_social: data.razon_social, auto_send_ecf: data.auto_send_ecf,
      });
      toast.success("Configuración DGII guardada");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Selecciona un archivo .p12 o .pfx");
    if (!certPwd) return toast.error("Ingresa la contraseña del certificado");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("password", certPwd);
      fd.append("environment", certEnv);
      await api.post("/dgii/certificate/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificado validado y guardado cifrado");
      setCertPwd(""); if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const testCert = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post("/dgii/certificate/test");
      toast.success(`Certificado válido · CN: ${res.subject_cn || "—"}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  const removeCert = async () => {
    const ok = await confirm({
      title: "¿Eliminar certificado?",
      description: "Ya no podrás firmar e-CF hasta subir uno nuevo.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete("/dgii/certificate");
      toast.success("Certificado eliminado");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };
  const daysUntil = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };
  const expDays = daysUntil(certInfo.not_valid_after);
  const expWarn = expDays !== null && expDays <= 30;

  return (
    <Section title="Facturación electrónica DGII" subtitle="Configura el certificado digital y el ambiente para emitir e-CF (E31, E32) válidos ante la DGII.">
      {/* Padrón RNC DGII (nacional) */}
      <div className="mb-6">
        <DgiiRncRegistryPanel />
      </div>
      {/* Panel de estado del certificado */}
      <div
        className={`p-4 rounded-md mb-6 border ${
          !certInfo.configured ? "bg-amber-500/10 border-amber-500/30"
            : expWarn ? "bg-amber-500/10 border-amber-500/30"
            : "bg-success/10 border-success/30"
        }`}
        data-testid="cert-status-panel"
      >
        <div className="flex items-start gap-3">
          {certInfo.configured
            ? <ShieldCheck className={`w-5 h-5 mt-0.5 ${expWarn ? "text-amber-600" : "text-success"}`} />
            : <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          }
          <div className="flex-1">
            <div className="font-semibold text-sm">
              {certInfo.configured ? "Certificado digital cargado" : "Certificado no cargado"}
            </div>
            {certInfo.configured ? (
              <div className="text-xs text-muted-foreground mt-2 grid grid-cols-2 gap-x-6 gap-y-1" data-testid="cert-meta">
                <div><b>Titular:</b> {certInfo.subject_cn || "—"}</div>
                <div><b>Emisor:</b> {certInfo.issuer_cn || "—"}</div>
                <div><b>Serial:</b> <span className="font-mono text-[10px]">{certInfo.serial || "—"}</span></div>
                <div><b>Entorno:</b> {certInfo.environment || "TesteCF"}</div>
                <div><b>Válido desde:</b> {fmtDate(certInfo.not_valid_before)}</div>
                <div className={expWarn ? "text-amber-700 dark:text-amber-400 font-semibold" : ""}>
                  <b>Vence:</b> {fmtDate(certInfo.not_valid_after)}
                  {expDays !== null && (
                    <span className="ml-1">
                      ({expDays >= 0 ? `${expDays} días` : "VENCIDO"})
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-[10px]">
                  Subido por {certInfo.uploaded_by} el {fmtDate(certInfo.uploaded_at)}
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-0.5">
                Sube tu certificado digital (.p12 / .pfx) emitido por una CA autorizada por DGII (Camara TIC, Avansi, etc.) para firmar e-CF.
              </div>
            )}
          </div>
          {certInfo.configured && (
            <div className="flex flex-col gap-1">
              <Button variant="outline" size="sm" onClick={testCert} disabled={testing} data-testid="cert-test-btn">
                {testing ? "Probando…" : "Probar"}
              </Button>
              <Button variant="outline" size="sm" onClick={removeCert} data-testid="cert-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="overline">Ambiente</Label>
          <Select value={data.environment} onValueChange={(v) => setData({ ...data, environment: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="test">Pruebas (TesteCF)</SelectItem>
              <SelectItem value="production">Producción</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 pt-5"><Switch checked={data.auto_send_ecf} onCheckedChange={(v) => setData({ ...data, auto_send_ecf: v })} /><Label>Enviar e-CF automáticamente al emitir</Label></div>
        <div><Label className="overline">RNC del emisor</Label><Input value={data.rnc_emisor} onChange={(e) => setData({ ...data, rnc_emisor: e.target.value })} className="mt-1 font-mono" /></div>
        <div><Label className="overline">Razón social</Label><Input value={data.razon_social} onChange={(e) => setData({ ...data, razon_social: e.target.value })} className="mt-1" /></div>
      </div>

      <div className="mt-6"><Button onClick={save} className="bg-primary font-bold" data-testid="save-dgii-btn">Guardar configuración</Button></div>

      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="overline mb-1">Cargar / reemplazar certificado digital (.p12 / .pfx)</h4>
        <p className="text-[11px] text-muted-foreground mb-4">
          🔒 El certificado y la contraseña se guardan cifrados con Fernet.
          La contraseña no se expone al frontend ni queda en logs.
        </p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="overline">Archivo</Label>
            <Input type="file" accept=".p12,.pfx" ref={fileRef} className="mt-1" data-testid="cert-file" />
          </div>
          <div>
            <Label className="overline">Contraseña</Label>
            <Input type="password" value={certPwd} onChange={(e) => setCertPwd(e.target.value)} className="mt-1 font-mono" data-testid="cert-password" />
          </div>
          <div>
            <Label className="overline">Entorno</Label>
            <Select value={certEnv} onValueChange={setCertEnv}>
              <SelectTrigger className="mt-1" data-testid="cert-env"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TesteCF">TesteCF (Pruebas)</SelectItem>
                <SelectItem value="Produccion">Producción</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={upload} disabled={uploading} className="mt-4" variant="outline" data-testid="upload-cert-btn">
          <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Subiendo…" : "Subir y validar"}
        </Button>
      </div>
    </Section>
  );
}

function NCFTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type_code: "", description: "", prefix: "", current: 1, from_number: 1, to_number: "", is_electronic: true, active: true, environment: "pruebas", expires_at: "" });
  const [editingId, setEditingId] = useState(null);
  const confirm = useConfirm();

  const load = async () => setItems((await api.get("/settings/ncf-sequences")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const body = { ...form, is_electronic: true, current: Number(form.current), from_number: Number(form.from_number), to_number: Number(form.to_number) || 0, expires_at: form.expires_at || null };
      if (editingId) await api.put(`/settings/ncf-sequences/${editingId}`, body);
      else await api.post("/settings/ncf-sequences", body);
      toast.success("Guardado"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar secuencia?",
      description: "Los NCF ya emitidos con esta secuencia no se verán afectados, pero no podrás emitir más de este tipo.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/settings/ncf-sequences/${id}`); load();
  };

  const openNew = () => { setForm({ type_code: "", description: "", prefix: "", current: 1, from_number: 1, to_number: "", is_electronic: true, active: true, environment: "pruebas", expires_at: "" }); setEditingId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it, environment: it.environment || "pruebas", expires_at: it.expires_at || "" }); setEditingId(it.id); setOpen(true); };

  return (
    <Section title="Secuencias e-CF" subtitle="Rangos de comprobantes electrónicos autorizados por la DGII. Amelia solo emite e-CF.">
      <PurgeNonElectronicCard onDone={load} />
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="bg-primary font-bold" data-testid="new-ncf-btn"><Plus className="w-4 h-4 mr-1.5" /> Nueva secuencia</Button>
      </div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr><th className="text-left p-3">Tipo</th><th className="text-left p-3">Descripción</th><th className="text-left p-3">Prefijo</th><th className="text-right p-3">Actual</th><th className="text-right p-3">Hasta</th><th className="text-left p-3">Ambiente</th><th className="text-left p-3">Vence</th><th className="text-right p-3">Acciones</th></tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{s.type_code}</td>
                <td className="p-3 text-xs">{s.description}</td>
                <td className="p-3 data-cell">{s.prefix}</td>
                <td className="p-3 text-right data-cell">{s.current}</td>
                <td className="p-3 text-right data-cell">{s.to_number}</td>
                <td className="p-3 text-xs" data-testid={`ncf-env-${s.type_code}`}>
                  {s.is_electronic ? (
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${s.environment === "produccion" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {s.environment === "produccion" ? "Producción" : "Pruebas"}
                    </span>
                  ) : "—"}
                </td>
                <td className="p-3 text-xs data-cell" data-testid={`ncf-expires-${s.type_code}`}>{s.expires_at ? s.expires_at.slice(0, 10) : "—"}</td>
                <td className="p-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`ncf-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`ncf-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar" : "Nueva"} secuencia e-CF</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="overline">Tipo</Label><Input value={form.type_code} onChange={(e) => setForm({ ...form, type_code: e.target.value })} className="mt-1 font-mono" placeholder="E31 / E32 / …" /></div>
            <div><Label className="overline">Prefijo</Label><Input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} className="mt-1 font-mono" /></div>
            <div className="col-span-2"><Label className="overline">Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><Label className="overline">Desde</Label><Input type="number" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label className="overline">Hasta</Label><Input type="number" value={form.to_number} onChange={(e) => setForm({ ...form, to_number: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label className="overline">Actual</Label><Input type="number" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="mt-1 font-mono" /></div>
            {(
              <>
                <div>
                  <Label className="overline">Ambiente</Label>
                  <select value={form.environment || "pruebas"} onChange={(e) => setForm({ ...form, environment: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="ncf-environment-select">
                    <option value="pruebas">Pruebas / certificación</option>
                    <option value="produccion">Producción (autorizada DGII)</option>
                  </select>
                </div>
                <div><Label className="overline">Vence</Label><Input type="date" value={(form.expires_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 font-mono" data-testid="ncf-expires-input" /></div>
                <p className="col-span-2 text-xs text-muted-foreground">
                  En producción registra exactamente el rango y el vencimiento que te autorizó la DGII en la Oficina Virtual. Amelia usa automáticamente las secuencias del ambiente del certificado activo.
                </p>
              </>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={submit} className="bg-primary font-bold" data-testid="ncf-save-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "user", password: "", recovery_email: "", active: true });
  const [editingId, setEditingId] = useState(null);
  const [rolesMeta, setRolesMeta] = useState({}); // {key: {label, is_system, description}}
  const confirm = useConfirm();

  const load = async () => setItems((await api.get("/settings/users")).data);
  const loadRoles = async () => {
    try {
      const { data } = await api.get("/settings/roles");
      setRolesMeta(data.meta || {});
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); loadRoles(); }, []);

  const rolesList = Object.entries(rolesMeta).sort((a, b) => {
    // system primero, luego alfabético
    if (a[1].is_system !== b[1].is_system) return a[1].is_system ? -1 : 1;
    return (a[1].label || a[0]).localeCompare(b[1].label || b[0]);
  });

  const submit = async () => {
    try {
      if (editingId) await api.put(`/settings/users/${editingId}`, form);
      else await api.post("/settings/users", form);
      toast.success("Guardado"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const openNew = () => { setForm({ email: "", name: "", role: "user", password: "", recovery_email: "", active: true }); setEditingId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditingId(u.id); setOpen(true); };
  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar usuario?",
      description: "El usuario perderá el acceso al sistema inmediatamente.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/settings/users/${id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Section title="Usuarios" subtitle="Gestiona quién puede acceder al sistema y con qué permisos.">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="bg-primary font-bold" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Nuevo usuario</Button>
      </div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Nombre</th><th className="text-left p-3">Rol</th><th className="text-left p-3">Email recuperación</th><th className="text-center p-3">Estado</th><th className="text-right p-3">Acciones</th></tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell text-xs font-bold">{u.email}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-muted"}`}>{u.role}</span></td>
                <td className="p-3 text-xs text-muted-foreground">{u.recovery_email || "—"}</td>
                <td className="p-3 text-center text-xs">{u.active ? "Activo" : "Inactivo"}</td>
                <td className="p-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(u.id)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label className="overline">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" disabled={!!editingId} /></div>
            <div><Label className="overline">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div>
              <Label className="overline">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {rolesList.map(([key, meta]) => (
                    <SelectItem key={key} value={key} data-testid={`user-role-option-${key}`}>
                      {meta.label || key}
                      {!meta.is_system && <span className="text-[10px] ml-1 opacity-60">(personalizado)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {rolesMeta[form.role]?.description && (
                <p className="text-[10px] text-muted-foreground mt-1">{rolesMeta[form.role].description}</p>
              )}
            </div>
            <div className="col-span-2"><Label className="overline">Email de recuperación</Label><Input value={form.recovery_email} onChange={(e) => setForm({ ...form, recovery_email: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2"><Label className="overline">{editingId ? "Nueva contraseña (opcional)" : "Contraseña"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 font-mono" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Activo</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={submit} className="bg-primary font-bold">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function FleetListPanel({ kind, title, subtitle, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", active: true, notes: "" });
  const confirm = useConfirm();

  const load = async () => {
    try { setItems((await api.get(`/settings/fleet/${kind}`)).data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", active: true, notes: "" }); setEditingId(null); setOpen(true); };
  const openEdit = (row) => { setForm({ name: row.name, active: row.active, notes: row.notes || "" }); setEditingId(row.id); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    try {
      if (editingId) {
        await api.put(`/settings/fleet/${kind}/${editingId}`, form);
        toast.success("Actualizado");
      } else {
        await api.post(`/settings/fleet/${kind}`, form);
        toast.success("Creado");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `¿Eliminar "${row.name}"?`,
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/settings/fleet/${kind}/${row.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Section title={title} subtitle={subtitle}>
      <div className="mb-4 flex justify-end">
        <Button onClick={openNew} className="bg-primary font-bold" data-testid={`${testidPrefix}-new-btn`}>
          <Plus className="w-4 h-4 mr-1.5" /> Nuevo
        </Button>
      </div>
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border bg-muted/30">
            <tr>
              <th className="text-left p-3">Nombre</th>
              <th className="text-left p-3">Notas</th>
              <th className="text-center p-3 w-24">Activo</th>
              <th className="text-right p-3 w-32">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 font-semibold">{row.name}</td>
                <td className="p-3 text-xs text-muted-foreground truncate max-w-[300px]">{row.notes || "—"}</td>
                <td className="p-3 text-center">
                  {row.active
                    ? <span className="inline-block px-2 py-0.5 text-[10px] rounded bg-emerald-600/10 text-emerald-700 dark:text-emerald-400">Activo</span>
                    : <span className="inline-block px-2 py-0.5 text-[10px] rounded bg-muted text-muted-foreground">Inactivo</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(row)} data-testid={`${testidPrefix}-edit-${row.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(row)} data-testid={`${testidPrefix}-delete-${row.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Sin registros. Crea el primero con &quot;Nuevo&quot;.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Nuevo"} {kind === "drivers" ? "chofer" : "vehículo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1"
                autoFocus
                data-testid={`${testidPrefix}-form-name`}
              />
            </div>
            <div>
              <Label className="overline">Notas (opcional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1"
                placeholder={kind === "drivers" ? "Ej: cel 809-…" : "Ej: placa, marca, modelo"}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              />
              <span className="text-sm">Activo</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-primary font-bold" data-testid={`${testidPrefix}-save`}>
              {editingId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function FleetTab() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <FleetListPanel
        kind="drivers"
        title="Choferes"
        subtitle="Personas que despachan mercancía. Aparecen en el dropdown al crear un conduce. El chofer más usado en los últimos 7 días se elige por defecto."
        testidPrefix="drivers"
      />
      <FleetListPanel
        kind="vehicles"
        title="Vehículos"
        subtitle="Camiones o motos usados para despacho. Aparecen en el dropdown al crear un conduce. El más usado en los últimos 7 días se elige por defecto."
        testidPrefix="vehicles"
      />
    </div>
  );
}


const SETTINGS_TABS = [
  { id: "company",     label: "Empresa",             icon: Building2,     desc: "Datos del negocio" },
  { id: "modules",     label: "Módulos & Branding",  icon: Package2,      desc: "Activa/desactiva funcionalidades" },
  { id: "pos",         label: "POS",                  icon: ScanBarcode,   desc: "Cliente por defecto en ventas" },
  { id: "tax",         label: "Impuestos",           icon: Percent,       desc: "ITBIS y tasas" },
  { id: "credit",      label: "Crédito",             icon: CreditCard,    desc: "Días de crédito por defecto" },
  { id: "pricing",     label: "Márgenes",             icon: Percent,       desc: "Márgenes estándar y mínimo" },
  { id: "quotation",   label: "Cotizaciones",         icon: FileClock,     desc: "Vigencia por defecto y máxima" },
  { id: "dgii",        label: "DGII / e-CF",         icon: FileBadge,     desc: "Certificado digital" },
  { id: "certification", label: "Certificación DGII", icon: FileCheck2,   desc: "Set de Pruebas Fase 2" },
  { id: "acecf",       label: "Certif. Aprobaciones", icon: FileCheck2,   desc: "Paso 3: ACECF (11 casos)" },
  { id: "phase4",      label: "Certif. Simulación",   icon: FileCheck2,   desc: "Paso 4: Operaciones reales" },
  { id: "ncf",         label: "Secuencias e-CF",     icon: Hash,          desc: "Rangos electrónicos" },
  { id: "email",       label: "Email",               icon: Mail,          desc: "Envío de PDFs" },
  { id: "lowstock",    label: "Existencias",         icon: AlertTriangle, desc: "Umbral de alerta" },
  { id: "expense_cats", label: "Categorías de gasto", icon: Tag,          desc: "Mapea gastos a cuentas GL" },
  { id: "printformat", label: "Formatos de impresión", icon: Printer,     desc: "Carta / Ticket 80mm" },
  { id: "qztray",      label: "QZ Tray (impresora térmica)", icon: Receipt, desc: "Impresión sin papel desperdiciado" },
  { id: "fleet",       label: "Choferes y Vehículos", icon: Truck,        desc: "Flota para conduces" },
  { id: "users",       label: "Usuarios",            icon: UsersIcon,     desc: "Acceso y roles" },
  { id: "data",        label: "Datos / Migración",   icon: Database,      desc: "CSV import/export" },
];

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "company";
  const setTab = (v) => setSearchParams(v === "company" ? {} : { tab: v }, { replace: true });

  return (
    <PageContainer>
      <PageHeader title="Ajustes" subtitle="Empresa, impuestos, certificado DGII, secuencias NCF, email, usuarios y migración de datos." testid="settings-header" />

      <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-5">
        {/* Cards horizontales con wrap automático y sticky al hacer scroll */}
        <TabsList
          className="sticky top-0 z-20 h-auto bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border flex flex-wrap gap-2 p-3 -mx-4 px-4 sm:mx-0 sm:px-3 sm:rounded-md sm:border justify-start"
          data-testid="settings-tabs-list"
        >
          {SETTINGS_TABS.map(({ id, label, icon: Icon, desc }) => (
            <TabsTrigger
              key={id}
              value={id}
              data-testid={`settings-tab-${id}`}
              className="
                group relative flex items-start gap-3 px-3.5 py-2.5
                min-w-[170px] max-w-[220px] flex-1 sm:flex-none
                rounded-md border border-border bg-card text-left transition-all
                hover:border-primary/40 hover:bg-accent/40
                data-[state=active]:border-primary data-[state=active]:bg-primary/10
                data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-primary/20
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
              "
            >
              <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground group-data-[state=active]:text-primary" />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold leading-tight text-foreground group-data-[state=active]:text-primary">
                  {label}
                </span>
                <span className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
                  {desc}
                </span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Panel de ancho completo */}
        <div className="w-full">
          <TabsContent value="company"><CompanyTab /></TabsContent>
          <TabsContent value="modules"><ModulesTab /></TabsContent>
          <TabsContent value="pos"><POSTab /></TabsContent>
          <TabsContent value="tax"><TaxTab /></TabsContent>
          <TabsContent value="credit"><CreditTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="quotation"><QuotationTab /></TabsContent>
          <TabsContent value="dgii"><DGIITab /></TabsContent>
          <TabsContent value="certification"><DGIICertificationTab /></TabsContent>
          <TabsContent value="acecf"><ACECFCertificationTab /></TabsContent>
          <TabsContent value="phase4"><Phase4CertificationTab /></TabsContent>
          <TabsContent value="ncf"><NCFTab /></TabsContent>
          <TabsContent value="email"><EmailTab /></TabsContent>
          <TabsContent value="lowstock"><LowStockTab /></TabsContent>
          <TabsContent value="expense_cats"><ExpenseCategoriesTab /></TabsContent>
          <TabsContent value="printformat"><PrintFormatTab /></TabsContent>
          <TabsContent value="qztray"><QZTrayTab /></TabsContent>
          <TabsContent value="fleet"><FleetTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="data"><MigrationContent /></TabsContent>
        </div>
      </Tabs>
    </PageContainer>
  );
}
